import { useState, useCallback } from 'react';
import Video from 'twilio-video';

export const useVideoTracks = (room) => {
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);

  const toggleVideo = useCallback(() => {
    if (room?.localParticipant) {
      const videoTracks = Array.from(room.localParticipant.videoTracks.values());
      videoTracks.forEach(publication => {
        if (publication.track) {
          if (isVideoEnabled) {
            publication.track.disable();
          } else {
            publication.track.enable();
          }
        }
      });
      setIsVideoEnabled(!isVideoEnabled);
    }
  }, [room, isVideoEnabled]);

  const toggleAudio = useCallback(() => {
    if (room?.localParticipant) {
      const audioTracks = Array.from(room.localParticipant.audioTracks.values());
      audioTracks.forEach(publication => {
        if (publication.track) {
          if (isAudioEnabled) {
            publication.track.disable();
          } else {
            publication.track.enable();
          }
        }
      });
      setIsAudioEnabled(!isAudioEnabled);
    }
  }, [room, isAudioEnabled]);

  const replaceVideoTrack = useCallback(async (deviceId) => {
    if (!room || !room.localParticipant) return;

    try {
      const tracks = await Video.createLocalTracks({
        video: { deviceId: { exact: deviceId } },
        audio: false
      });
      
      const newTrack = tracks.find(t => t.kind === 'video');
      if (!newTrack) return;

      const oldTrackPub = Array.from(room.localParticipant.videoTracks.values())[0];
      if (oldTrackPub) {
        room.localParticipant.unpublishTrack(oldTrackPub.track);
        if (oldTrackPub.track && typeof oldTrackPub.track.stop === 'function') {
          oldTrackPub.track.stop();
        }
      }

      room.localParticipant.publishTrack(newTrack);
      setIsVideoEnabled(true);
    } catch (error) {
      console.error('Error replacing video track:', error);
    }
  }, [room]);

  const replaceAudioTrack = useCallback(async (deviceId) => {
    if (!room || !room.localParticipant) return;

    try {
      const tracks = await Video.createLocalTracks({
        audio: { deviceId: { exact: deviceId } },
        video: false
      });
      
      const newTrack = tracks.find(t => t.kind === 'audio');
      if (!newTrack) return;

      const oldTrackPub = Array.from(room.localParticipant.audioTracks.values())[0];
      if (oldTrackPub) {
        room.localParticipant.unpublishTrack(oldTrackPub.track);
        if (oldTrackPub.track && typeof oldTrackPub.track.stop === 'function') {
          oldTrackPub.track.stop();
        }
      }

      room.localParticipant.publishTrack(newTrack);
      setIsAudioEnabled(true);
    } catch (error) {
      console.error('Error replacing audio track:', error);
    }
  }, [room]);

  const createLocalTracks = useCallback(async (videoDeviceId, audioDeviceId) => {
    try {
      const tracks = await Video.createLocalTracks({
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : undefined,
        video: videoDeviceId ? { 
          deviceId: { exact: videoDeviceId },
          width: { min: 640, ideal: 1280, max: 1920 }, 
          height: { min: 480, ideal: 720, max: 1080 }, 
          frameRate: { min: 15, ideal: 30, max: 60 }
        } : undefined
      });

      const videoTrack = tracks.find(t => t.kind === 'video');
      const audioTrack = tracks.find(t => t.kind === 'audio');

      setIsVideoEnabled(!!videoTrack);
      setIsAudioEnabled(!!audioTrack);

      return { videoTrack, audioTrack, allTracks: tracks };
    } catch (error) {
      console.error('Error creating local tracks:', error);
      return { videoTrack: null, audioTrack: null, allTracks: [] };
    }
  }, []);

  const stopAllTracks = useCallback((tracks) => {
    tracks.forEach(track => {
      if (track && typeof track.stop === 'function') {
        track.stop();
      }
    });
  }, []);

  return {
    isVideoEnabled,
    isAudioEnabled,
    toggleVideo,
    toggleAudio,
    replaceVideoTrack,
    replaceAudioTrack,
    createLocalTracks,
    stopAllTracks,
    setIsVideoEnabled,
    setIsAudioEnabled
  };
}; 